import React, { useEffect, useState } from 'react';
import { getCachedShop, fetchShop, saveProduct, deleteProduct } from '../shopStore';

const money = (n) => `$${(Number(n) || 0).toLocaleString()}`;
const blank = () => ({ id: '', name: '', price: '', kind: 'device', deviceCount: 1, active: true, description: '', perks: [''] });

// Admin: manage the devices customers can buy in the app. Add/edit name, what it
// does, the perks, and price. Saved server-side so every customer sees the change.
export default function ProductsPage() {
  const [products, setProducts] = useState(() => getCachedShop().products);
  const [editing, setEditing] = useState(null); // product being added/edited
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const reload = () => fetchShop().then((s) => setProducts(s.products)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const startNew = () => { setErr(''); setEditing(blank()); };
  const startEdit = (p) => { setErr(''); setEditing({ ...p, perks: p.perks && p.perks.length ? [...p.perks] : [''] }); };

  const save = async () => {
    if (!editing.name.trim()) { setErr('Give the device a name.'); return; }
    setBusy(true); setErr('');
    try {
      await saveProduct({ ...editing, perks: (editing.perks || []).map((s) => s.trim()).filter(Boolean) });
      await reload();
      setEditing(null);
    } catch (e) { setErr(e.message || 'Could not save.'); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this device from the shop?')) return;
    setBusy(true);
    try { await deleteProduct(id); await reload(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: '28px 26px 80px', maxWidth: '1000px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <h1 style={{ fontSize: '24px', color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>🏷️ Products</h1>
        <button onClick={startNew} style={btn}>+ New device</button>
      </div>
      <p style={{ color: '#64748b', margin: '0 0 20px' }}>These are the devices customers can buy from the in-app Shop.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
        {products.length === 0 && <div style={{ color: '#94a3b8' }}>No products yet — add your first device.</div>}
        {products.map((p) => (
          <div key={p.id} style={{ ...card, opacity: p.active === false ? 0.6 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <h2 style={{ fontSize: '16px', color: '#0f172a', margin: 0 }}>{p.name}</h2>
              <span style={{ fontWeight: 800, color: '#0066ff', whiteSpace: 'nowrap' }}>{money(p.price)}</span>
            </div>
            {p.active === false && <span style={{ fontSize: '11px', color: '#b45309', background: '#fffbeb', borderRadius: '4px', padding: '1px 6px' }}>Hidden</span>}
            {p.description && <p style={{ color: '#475569', fontSize: '13px', margin: '8px 0', lineHeight: 1.5 }}>{p.description}</p>}
            {Array.isArray(p.perks) && p.perks.length > 0 && (
              <ul style={{ margin: '4px 0 10px', paddingLeft: '18px', color: '#334155', fontSize: '12.5px', lineHeight: 1.5 }}>
                {p.perks.map((perk, i) => <li key={i}>{perk}</li>)}
              </ul>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button onClick={() => startEdit(p)} style={{ ...btnGhost, flex: 1 }}>Edit</button>
              <button onClick={() => remove(p.id)} style={{ ...btnGhost, color: '#dc2626', borderColor: '#fecaca' }}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={modalWrap} onClick={() => !busy && setEditing(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: '18px', color: '#0f172a', margin: '0 0 14px' }}>{editing.id ? 'Edit device' : 'New device'}</h2>
            <label style={lbl}>Name</label>
            <input style={inp} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. TagAlong OBD Tracker" />
            <label style={lbl}>What it does</label>
            <textarea style={{ ...inp, minHeight: '64px', resize: 'vertical' }} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Short description shown on the product card" />
            <label style={lbl}>Perks (one per line)</label>
            {(editing.perks || ['']).map((perk, i) => (
              <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                <input style={inp} value={perk} onChange={(e) => { const perks = [...editing.perks]; perks[i] = e.target.value; setEditing({ ...editing, perks }); }} placeholder={`Perk ${i + 1}`} />
                <button onClick={() => setEditing({ ...editing, perks: editing.perks.filter((_, j) => j !== i) })} style={{ ...btnGhost, padding: '4px 10px' }}>✕</button>
              </div>
            ))}
            <button onClick={() => setEditing({ ...editing, perks: [...(editing.perks || []), ''] })} style={{ ...btnGhost, marginBottom: '12px' }}>+ Add perk</button>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Price (USD)</label>
                <input style={inp} type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} placeholder="149" />
              </div>
              <div style={{ width: '150px' }}>
                <label style={lbl}>Type</label>
                <select style={inp} value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value, deviceCount: e.target.value === 'device' ? 1 : 0 })}>
                  <option value="device">Device (ships hardware)</option>
                  <option value="plan">Plan / membership</option>
                </select>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
              <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
              Show in the customer shop
            </label>
            {err && <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '10px' }}>{err}</div>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button onClick={() => setEditing(null)} style={{ ...btnGhost, flex: 1 }}>Cancel</button>
              <button onClick={save} disabled={busy} style={{ ...btn, flex: 2, background: busy ? '#94a3b8' : btn.background }}>{busy ? 'Saving…' : 'Save device'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' };
const btn = { background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, border: 'none', borderRadius: '10px', padding: '9px 16px', cursor: 'pointer', fontSize: '14px' };
const btnGhost = { background: 'white', color: '#0f172a', fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontSize: '13px' };
const modalWrap = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 6000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' };
const modal = { background: 'white', borderRadius: '16px', padding: '22px', width: 'min(520px, 96vw)', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' };
const lbl = { display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '10px 0 4px' };
const inp = { border: '1px solid #cbd5e1', borderRadius: '8px', padding: '9px 11px', fontSize: '14px', width: '100%', boxSizing: 'border-box' };
